import { useCallback, useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

type CatalogModel = {
  id: string;
  family: string;
  version: string;
  tool_calls: boolean;
  streaming: boolean;
};

type Overrides = Record<string, string>;

const TIERS = ["deep", "standard", "cheap"] as const;
const AUTO = "__auto__";

export function ModelSettingsSection() {
  const [models, setModels] = useState<CatalogModel[]>([]);
  const [overrides, setOverrides] = useState<Overrides>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mRes, oRes] = await Promise.all([
        fetch("/api/models"),
        fetch("/api/settings/models"),
      ]);
      if (!mRes.ok) throw new Error(`models: ${mRes.status}`);
      if (!oRes.ok) throw new Error(`settings: ${oRes.status}`);
      setModels(await mRes.json());
      const o = await oRes.json();
      setOverrides(o.overrides ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setTier = (tier: string, value: string) => {
    setSaved(false);
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === AUTO) delete next[tier];
      else next[tier] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/settings/models", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ overrides }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `save failed: ${res.status}`);
      }
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading models…</p>;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-medium">Model overrides</h3>
        <p className="text-sm text-muted-foreground">
          Pin a tier to a specific catalog model, or leave it on automatic
          selection.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex flex-col gap-3">
        {TIERS.map((tier) => (
          <div key={tier} className="flex items-center gap-3">
            <label htmlFor={`tier-${tier}`} className="w-24 text-sm capitalize">
              {tier}
            </label>
            <Select
              value={overrides[tier] ?? AUTO}
              onValueChange={(v) => setTier(tier, v)}
            >
              <SelectTrigger id={`tier-${tier}`} className="flex-1">
                <SelectValue placeholder="Automatic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AUTO}>Automatic (deterministic)</SelectItem>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
        {saved && <span className="text-sm text-muted-foreground">Saved.</span>}
      </div>
    </div>
  );
}
